<template>
    <div>
        <div @change="updateValid" class="flex-grid flex-grid--wrap a-form-inputs">
            <form @submit.prevent class="m-form">
                <div class="flex-grid__item">
                    <control for="discount_rate" label="Discount Rate">
                        <select name="discount_rate" id="discount_rate" v-model="discountRate" v-field-control
                                required>
                            <option :value="-2.0">-2.00%</option>
                            <option :value="-1.5">-1.50%</option>
                            <option :value="-1.0">-1.00%</option>
                            <option :value="-0.75">-0.75%</option>
                            <option :value="-0.5">-0.50%</option>
                            <option :value="-0.25">-0.25%</option>
                            <option :value="0.0">0.00%</option>
                            <option :value="0.5">0.50%</option>
                            <option :value="1.0">1.00%</option>
                            <option :value="1.5">1.50%</option>
                            <option :value="2.0">2.00%</option>
                            <option :value="2.5">2.50%</option>
                            <option :value="3.0">3.00%</option>
                        </select>
                        <control-error :show="$error('discount_rate')">
                            Discount rate is required
                        </control-error>
                    </control>
                    <control name="description_of_services" label="Description of Service">
                        <input autocomplete="off" name="description_of_services"
                               v-model="service.descriptionOfService"
                               v-field-control
                               @input="update"
                               maxlength="100"
                               id="description_of_services"
                               type="text">
                    </control>
                    <control for="multiplicand" label="Multiplicand" before-after="pound">
                        <input v-model.number="service.multiplicand"
                               @input="update"
                               v-on:blur="service.multiplicand = service.multiplicand || 0"
                               required
                               v-two-dp
                               name="multiplicand">
                        <control-error :show="$error('multiplicand')">
                            The multiplicand is required
                        </control-error>
                    </control>
                    <control for="duration_type" label="Dependency for life">
                        <select name="duration_type" id="duration_type" v-model="durationMask">
                            <option :value="true">Yes</option>
                            <option :value="false">No</option>
                        </select>
                    </control>
                    <control label="Duration of Dependency" for="method1" v-if="!durationMask">
                        <select id="method1" v-model="service.durationType" @change="update">
                            <option value="" disabled hidden>Please select...</option>
                            <option value="years_and_months">Enter Years/Months</option>
                            <option value="age">Enter Age at End of Dependency</option>
                            <option value="date">Enter Date</option>
                        </select>
                    </control>
                    <control label="Enter Years/Months From the Start of Dependency" v-if="service.durationType=='years_and_months'">
                        <div class="a-form-inputs--half">
                            <div class="a-form-inputs__container">
                                <select id="years" v-model.number="service.years"
                                        @change="update">
                                    <option v-for="year in 51" :value="year - 1">{{ year - 1}} year(s)</option>
                                </select>
                            </div>
                            <div class="a-form-inputs__container">
                                <select id="months" v-model.number="service.months"
                                        @change="update">
                                    <option v-for="month in 12" :value="month - 1">
                                        {{ month - 1 }} month(s)
                                    </option>
                                </select>
                            </div>
                        </div>
                    </control>
                    <control label="Age at end of Dependency" for="age_at_end"
                             before-after="years-old" v-if="service.durationType=='age'">
                        <input v-model.number="service.age"
                               v-two-dp
                               @input="update"
                               required
                               min="20"
                               name="age_at_end"
                               type="number">
                    </control>
                    <control for="services_end_of_dependency" label="End of Dependency">
                        <div class="a-form-inputs--half a-form-inputs--half-to">
                            <div class="a-form-inputs__container">
                                <date-input v-model="service.toDate"
                                            :disabled="service.durationType!='date'"
                                            v-field-control:services_end_of_dependency.required="{type:'date', rules:servicesEndOfDependencyRules}"
                                            @input="update"></date-input>
                                <control-error :show="$error('services_end_of_dependency')">
                                    End of Dependency is required, must be in the format DD/MM/YYYY, cannot be after the
                                    Deceased's End of Dependency ({{ theDeceased.lifeExpectancy.dateOfDeath }})
                                    nor before the Deceased's Date of Death ({{ theDeceased.dateOfDeath }})
                                </control-error>
                            </div>
                            <div class="a-form-inputs__to">for</div>
                            <div class="a-form-inputs__container a-form-inputs--years">
                                <input autocomplete="off" type="number" v-model="service.duration" @input="update"
                                       :disabled="service.durationType=='life'"
                                       v-field-control name="duration"
                                       v-field-control:duration.required="{type:'number'}"
                                       min="0" v-two-dp>
                                <control-error :show="$error('duration')">
                                    Length of Dependency is required and must be at least one year
                                </control-error>
                            </div>
                        </div>
                    </control>
                </div>
                <div class="flex-grid__item flex-grid--space-left">
                    <control label="Select Ogden Table">
                        <select v-model="discountTable">
                            <option :value="1">Table 1 or 2</option>
                            <option :value="28">Table 36</option>
                        </select>
                    </control>
                    <control :label="table1Or2Label"
                             for="ogden_table_multiplier"
                             v-if="service.discountTable === 1">
                        <input autocomplete="off" readonly disabled type="text" :value="dependencyOnIncome.table1Or2Multiplier" v-two-dp>
                    </control>
                    <control label="Ogden Table 36 Multiplier"
                             for="ogden_table_multiplier"
                             v-if="service.discountTable === 28">
                        <input autocomplete="off" readonly disabled type="text" :value="service.ogdenTableTwentyEight" v-two-dp>
                    </control>
                    <control for="ogden_table_e_pre_assessment" label="Ogden Table E Pre-Assessment Discount"
                             :tip-icon="'formula'"
                             tip-text="Click to view calculation" tip-side="left"
                             tip-width="140"
                             tip-name="ogden_table_e_pre_assessment"
                             :modal-name="'services_' + uniqueId"
                             :endpoint="'pre'"
                             :payload="preAndPostAssessmentPayload">
                        <input autocomplete="off" id="ogden_table_e_pre_assessment"
                               :value="service.ogdenTableE"
                               name="ogden_table_e_pre_assessment"
                               v-two-dp
                               type="text" disabled>
                    </control>
                    <control for="ogden_table_f_pre_assessment" label="Ogden Table F Post-Assessment Discount"
                             :tip-icon="'formula'"
                             tip-text="Click to view calculation" tip-side="left"
                             tip-width="140"
                             tip-name="ogden_table_f_pre_assessment"
                             :modal-name="'services_' + uniqueId"
                             :endpoint="'post'"
                             :payload="preAndPostAssessmentPayload">
                        <input autocomplete="off" id="ogden_table_f_pre_assessment"
                               :value="service.ogdenTableF"
                               name="ogden_table_f_pre_assessment"
                               v-two-dp disabled>
                    </control>
                    <div :class="{ 'a-loader--total' : loading }"></div>
                    <control for="calculated_services" label="Calculated Services" before-after="pound" v-if="!loading">
                        <input autocomplete="off" :value="currencyFormat(calculatedServices, '')"
                               min="0.00"
                               disabled
                               type="text">
                    </control>
                </div>
            </form>
            <div class="flex-grid__g-8">
                <button class="a-button" @click="deleteThisService">Delete Service</button>
                <button class="a-button" @click="addService">Add Service</button>
            </div>
        </div>
        <div class="a-table--really-wide">
            <div :class="{ 'a-loader--table' : servicesTableUpdating() }"></div>
            <table class="a-table a-table--full-width" cellpadding="0" cellspacing="0"
                   v-if="table && table.pre && table.post && !servicesTableUpdating()">
                <thead>
                <tr>
                    <th>Period</th>
                    <th>Duration (years)</th>
                    <th>Dependency Multiplicand</th>
                    <th>Multiplier</th>
                    <th>Discount Factor</th>
                    <th>Discounted Multiplier</th>
                    <th>Dependency Value</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>Pre-Assessment period</td>
                    <td>{{ roundValue(table.pre.multiplier, 3) }}</td>
                    <td>{{ currencyFormat(table.pre.multiplicand) }}</td>
                    <td>{{ roundValue(table.pre.multiplier, 3)}}</td>
                    <td>{{ roundValue(table.pre.discount, 3) }}</td>
                    <td>
                        {{ roundValue(table.pre.discount, 3)}} x {{ roundValue(table.pre.duration, 3) }} =
                        {{ roundValue(table.pre.discount * table.pre.multiplier, 3) }}
                    </td>
                    <td>{{ currencyFormat(table.pre.dependencyValue) }}</td>
                </tr>
                <tr v-if="table.post.multiplier > 0">
                    <td>Post-Assessment period</td>
                    <td>{{ roundValue(table.post.duration, 3) }}</td>
                    <td>{{ currencyFormat(table.post.multiplicand) }}</td>
                    <td>{{ roundValue(table.post.multiplier, 3)}}</td>
                    <td>{{ roundValue(table.post.discount, 3) }}</td>
                    <td>
                        {{ roundValue(table.post.discount, 3)}} x {{ roundValue(table.post.multiplier, 3) }} =
                        {{ roundValue(table.post.discount * table.post.multiplier, 3) }}
                    </td>
                    <td>{{ currencyFormat(table.post.dependencyValue) }}</td>
                </tr>
                </tbody>
            </table>
        </div>

        <modal :name="'services_' + uniqueId" no-default-button="true">
            <modal-content></modal-content>
        </modal>
    </div>
</template>

<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import DateInput from 'components/form/control/DateInput.vue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import FormValidator from 'components/form/validator/mixin';
    import Vue from 'vue';
    import {setStepValid} from 'services/routingMethods';
    import roundValue from 'mixins/roundValue';
    import currencyFormat from 'mixins/currencyFormat';
    import {mapActions, mapGetters} from 'vuex';
    import _ from "lodash";
    import Modal from 'components/modal/Modal.vue';
    import ModalContent from 'components/modal/ModalContent.vue';

    export default {
        name: 'service',
        props: ['index'],
        components: {
            Control,
            ControlError,
            DateInput,
            Modal,
            ModalContent
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        mixins: [
            FormValidator,
            roundValue,
            currencyFormat
        ],
        mounted() {
            if(this.table) {
                this.update()
            }
        },
        created() {
            this.updateValid();
        },
        computed: {
            ...mapGetters('fatal/dependency/dependencyOnIncome', {
                dependencyOnIncome: 'getAll'
            }),
            ...mapGetters('fatal/dependency/claimDetails', {
                detailValue: 'value',
            }),
            ...mapGetters('http', [
                'apiRouteRequestRunning',
            ]),
            ...mapGetters('fatal/dependency/deceased', [
                'theDeceased',
            ]),
            ...mapGetters('http', ['running']),
            ...mapGetters('fatal/dependency/dependencyOnServices', {
                findService: 'find',
                findTable: 'table',
                allServicesProcessed: 'allServicesProcessed',
                getServicesStatus: 'getServicesStatus'
            }),
            ...mapGetters('fatal/dependency/dependants', ['preTrialDependency']),
            loading: {
                get() {
                    return this.getServicesStatus;
                }
            },
            service() {
                let service = {};
                Vue.util.extend(service, this.findService(this.index));
                return service;
            },
            table() {
                return this.findTable(this.index);
            },
            table1Or2Label: {
                get() {
                    return 'Ogden Table ' + (this.theDeceased.gender === 'female' ? 2 : 1) + ' Multiplier';
                }
            },
            discountRate: {
                get() {
                    return this.detailValue('discountRate');
                },
                set(value) {
                    this.updateDetail({key: 'discountRate', value});
                    return this.updateDiscountTable(this.dependencyOnIncome.discountTable);
                }
            },
            discountTable: {
                get() {
                    return this.dependencyOnIncome.discountTable;
                },
                set(value) {
                    return this.updateDiscountTable(value);
                }
            },
            uniqueId() {
                return this._uid;
            },
            calculatedServices() {
                return this.table ? this.table.pre.dependencyValue + this.table.post.dependencyValue : 0;
            },
            servicesEndOfDependencyRules() {
                return "min:" + this.theDeceased.dateOfDeath +
                    '|' +
                    "max:" + this.theDeceased.lifeExpectancy.dateOfDeath;
            },
            durationMask: {
                get() {
                    return this.service.durationType === 'life';
                },
                set(value) {
                    if (value) {
                        this.service.durationType = 'life';
                    }
                    else if (this.service.durationType === 'life') {
                        this.service.durationType = 'years_and_months';
                    }
                    this.update();
                }
            },
            preAndPostAssessmentPayload() {
                return {
                    assumedSettlement: this.detailValue('assumedSettlement'),
                    person: this.theDeceased,
                }
            },
        },
        methods: {
            ...mapActions('fatal/dependency/dependencyOnServices', {
                addService: 'add',
                updateService: 'update',
                deleteService: 'deleteService',
            }),
            ...mapActions('fatal/dependency', ['checkToUpdateMultipliers']),
            deleteThisService() {
                return this.deleteService(this.index).then(this.updateValid);
            },
            update() {
                this.updateService({index: this.index, service: this.service})
                    .then(this.updateValid)
                    .then(this.checkToUpdateMultipliers);
            },
            updateValid() {
                this.$emit('check');
            },
            ...mapActions('fatal/dependency/claimDetails', {
                updateDetail: 'update',
            }),
            ...mapActions('fatal/dependency/dependencyOnIncome', [
                'updateDiscountTable',
            ]),
            servicesTableUpdating() {
                return this.apiRouteRequestRunning('/api/v1/calculators/monetary/services/table');
            },
        }

    }

</script>


// WEBPACK FOOTER //
// Service.vue?b2f40bb2